'use client';

import LeyendaIlustracion from '@/components/marketing/comun/LeyendaIlustracion';
import { cn } from '@/lib/utils';
import type { ColumnaFuncion } from '@/services/asistenteService';

import { ContenidoFuncion, clasesFuncion, useTecladoPestanas } from './PestanasPoderesEscritorio';
import PoderesDemo from './PoderesDemo';
import { GRUPOS_PODERES, grupoDeFuncion, type IdFuncionPoder } from './gruposPoderes';
import { TEXTOS_PODERES } from './textosPoderes';

// Móvil de Poderes_Chat_Movil: los cuatro grupos son pestañas y debajo van las funciones del grupo
// como botones pulsables y la demo de la elegida. El grupo no es estado propio: se deriva de la
// función elegida, así que cambiar de grupo es elegir su primera función (el lienzo hace lo mismo
// para que la demo nunca enseñe una función de otro grupo).

interface PropiedadesPoderesMovil {
    idBase: string;
    seleccionada: IdFuncionPoder;
    alSeleccionar: (id: IdFuncionPoder) => void;
}

const COLUMNAS: readonly ColumnaFuncion[] = GRUPOS_PODERES.map((g) => g.columna);
const TECLAS_GRUPO = ['ArrowRight', 'ArrowLeft'] as const;

function primeraFuncion(columna: ColumnaFuncion): IdFuncionPoder {
    const grupo = GRUPOS_PODERES.find((g) => g.columna === columna);
    if (!grupo) throw new Error(`Poderes móvil: no hay grupo con la columna «${columna}»`);
    return grupo.funciones[0];
}

function PestanasGrupo({ idBase, seleccionada, alSeleccionar }: PropiedadesPoderesMovil) {
    const actual = grupoDeFuncion(seleccionada).columna;
    const teclado = useTecladoPestanas(COLUMNAS, (c) => alSeleccionar(primeraFuncion(c)), TECLAS_GRUPO);
    return (
        <div role="tablist" aria-label={TEXTOS_PODERES.etiquetaGrupos} className="grid grid-cols-4 gap-[6px]">
            {GRUPOS_PODERES.map(({ columna, nombre }) => {
                const activo = columna === actual;
                return (
                    <button
                        key={columna}
                        ref={teclado.registrar(columna)}
                        type="button"
                        role="tab"
                        id={`${idBase}-grupo-${columna}`}
                        data-grupo={columna}
                        aria-selected={activo}
                        aria-controls={`${idBase}-panel`}
                        tabIndex={activo ? 0 : -1}
                        onClick={() => alSeleccionar(primeraFuncion(columna))}
                        onKeyDown={(evento) => teclado.alPulsarTecla(columna, evento)}
                        className={cn(
                            'h-11 rounded-ds-md border-2 border-ink px-1 text-[13px]',
                            activo
                                ? 'bg-accent-blue font-bold text-on-accent-blue shadow-ds-chip'
                                : 'bg-surface-2 font-semibold text-ink',
                        )}
                    >
                        {nombre}
                    </button>
                );
            })}
        </div>
    );
}

function BotonesFuncion({ seleccionada, alSeleccionar }: Omit<PropiedadesPoderesMovil, 'idBase'>) {
    return (
        <div className="flex flex-col gap-[6px]">
            {grupoDeFuncion(seleccionada).funciones.map((id) => (
                <button
                    key={id}
                    type="button"
                    data-funcion={id}
                    aria-pressed={id === seleccionada}
                    onClick={() => alSeleccionar(id)}
                    className={cn(clasesFuncion(id === seleccionada), 'min-h-11')}
                >
                    <ContenidoFuncion id={id} seleccionada={id === seleccionada} />
                </button>
            ))}
        </div>
    );
}

export default function PoderesMovil(props: PropiedadesPoderesMovil) {
    const { idBase, seleccionada } = props;
    return (
        <>
            <PestanasGrupo {...props} />
            <div
                role="tabpanel"
                id={`${idBase}-panel`}
                aria-labelledby={`${idBase}-grupo-${grupoDeFuncion(seleccionada).columna}`}
                className="flex flex-col gap-4"
            >
                <BotonesFuncion seleccionada={seleccionada} alSeleccionar={props.alSeleccionar} />
                <div className="flex flex-col gap-ds-2">
                    <PoderesDemo funcion={seleccionada} ancho={358} alto={620} />
                    <LeyendaIlustracion />
                </div>
            </div>
        </>
    );
}
